import React, { useEffect, useState } from "react";
import { Button, Card, Select, Space } from "antd";
import { LeftOutlined } from "@ant-design/icons";
import { Line, Pie } from "react-chartjs-2";
import { Chart as ChartJS, registerables } from "chart.js";
import { Link, useLocation } from "react-router-dom";
import { routes } from "src/data/routes";
import { useAccount } from "src/hooks/account.hook";
import {
  ProductAllocation,
  ProductPerfomanceResponse,
  SchemeCapitalResponse,
} from "src/models/account";
import { getChartsData, getPieChartsData, intervals } from "src/data/account";
import { getChartsOptions, getPieChartsOptions } from "src/data/columns";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "src/hooks/analytics.hook";
import { usePageTracking } from "src/hooks/tracking-page.hook";

export default function ProductDetails() {
  ChartJS.register(...registerables);
  let { state } = useLocation();
  const {
    getAssetPerformanceEvolution,
    getSchemeCapital,
    getProductAllocation,
  } = useAccount();

  const [loading, setLoading] = useState<boolean>(false);

  const [interval, setInterval] = useState<string>("1 year");

  const [productPerformanceData, setProductPerformanceData] = useState<any>(
    getChartsData()
  );
  const [productPerformanceOptions, setProductPerformanceOptions] =
    useState<any>(getChartsOptions());

  const { trackPage } = usePageTracking();

  const [schemeCapitalData, setSchemeCapitalData] = useState<any>(
    getChartsData()
  );
  const [schemeCapitalOptions, setSchemeCapitalOptions] = useState<any>(
    getChartsOptions()
  );

  const [productAllocationLoading, setProductAllocationLoading] =
    useState<boolean>(false);

  const [productAllocationData, setProductAllocationData] = useState<any>(
    getPieChartsData()
  );
  const [productAllocationOptions, setProductAllocationOptions] = useState<any>(
    getPieChartsOptions()
  );
  const { t } = useTranslation("");

  const { track } = useAnalytics();

  useEffect(() => {
    getProductPerformanceEvolution();
    getProductSchemeCapital();
    getAllocation();
  }, []);
  useEffect(() => {
    trackPage();
  }, []);

  const getProductPerformanceEvolution = (data?: string) => {
    setLoading(true);
    getAssetPerformanceEvolution({
      schemeId: state?.product?.product_id,
      interval: data || interval,
    })
      .then((value: any[]) => {
        if (value) {
          setProductPerformanceData(
            getChartsData(
              value?.map((elt: ProductPerfomanceResponse) => elt?.date_current),
              value?.map(
                (elt: ProductPerfomanceResponse) => elt?.net_asset_value
              ),
              t('netAssetValue', { ns: 'products' })
            )
          );
          setProductPerformanceOptions(
            getChartsOptions(
              value?.map((elt: ProductPerfomanceResponse) => elt?.performance),
              t('performance', { ns: 'products' }),
              true
            )
          );
        }
      })
      .finally(() => setLoading(false));
  };

  const getProductSchemeCapital = (data?: string) => {
    setLoading(true);
    getSchemeCapital({
      schemeId: state?.product?.product_id,
      interval: data || interval,
    })
      .then((value: any[]) => {
        if (value) {
          setSchemeCapitalData(
            getChartsData(
              value?.map((elt: SchemeCapitalResponse) => elt?.date_current),
              value?.map((elt: SchemeCapitalResponse) => elt?.capital_balance)
            )
          );
          setSchemeCapitalOptions(
            getChartsOptions(
              value?.map(
                (elt: SchemeCapitalResponse) => elt?.unrealised_interest
              )
            )
          );
        }
      })
      .finally(() => setLoading(false));
  };

  const handleChange = (value: string) => {
    setInterval(value);
    getProductSchemeCapital(value);
    getProductPerformanceEvolution(value);
    track("Setting", {
      page_current: "product_details",
      element_type: "drop-down",
      setting_name: "time range",
      value_set: value,
      setting_affects: "page",
    });
  };
  const getAllocation = () => {
    setProductAllocationLoading(true);
    getProductAllocation(state?.product?.product_id)
      .then((value: any[]) => {
        if (value) {
          setProductAllocationData(
            getPieChartsData(
              value?.map((elt: ProductAllocation) => elt?.asset_name),
              value?.map((elt: ProductAllocation) => elt?.value_percent)
            )
          );
          setProductAllocationOptions(
            getPieChartsOptions(
              value?.map((elt: ProductAllocation) => elt?.asset_isin_code)
            )
          );
        }
      })
      .finally(() => setProductAllocationLoading(false));
  };
  return (
    <>
      <div>
        <Space className="flex items-center">
          <Link to={routes.PRODUCTS}>
            <Button
              type="link"
              className="text-lg md:text-xl"
              icon={<LeftOutlined />}
            >
              {t("goBack")}
            </Button>
          </Link>
          <h1 className="text-lg md:text-2xl">
            {t("products:productDetailLabel")} :{" "}
            <span>{state?.product.product_name}</span>
          </h1>
        </Space>
        <Card hoverable className="flex justify-center items-center mt-4 bg-op">
          <div className="flex justify-between w-full">
            <Space wrap>
              <h2 className="text-center text-lg md:text-xl font-bold">
                {t("timeRange.label")}
              </h2>
              <Select
                value={interval}
                disabled={loading}
                style={{ width: 120 }}
                onChange={handleChange}
                options={intervals.map((elt) => ({
                  value: elt.value,
                  label: t(elt.label),
                }))}
              />
            </Space>
          </div>
        </Card>
        <div className="mt-10 flex flex-col md:flex-row items-center justify-between gap-4 w-full">
          <Card
            hoverable
            className="flex justify-center items-center w-full md:w-1/2 bg-op"
          >
            <h1 className="text-lg md:text-xl">
              {t("products:productPerformanceEvolutionlabel")}
            </h1>

            <div className="w-full flex justify-center h-[300px]  mt-3">
              <Line
                options={productPerformanceOptions}
                data={productPerformanceData}
                className="w-full h-full"
              />
            </div>
          </Card>
          <Card
            hoverable
            className="flex justify-center items-center w-full md:w-1/2 bg-op"
          >
            <h1 className="text-lg md:text-xl">
              {t("products:productAllocationLabel")}
            </h1>
            <div className="w-full flex justify-center h-[300px]  mt-3">
              <Pie
                options={productAllocationOptions}
                data={productAllocationData}
                className="w-full h-full"
              />
            </div>
          </Card>
        </div>
        <Card
          hoverable
          className="flex justify-center items-center mt-10 w-full bg-op"
        >
          <h1 className="text-lg md:text-xl">{t("products:investment")}</h1>
          <div className="flex justify-between w-full"></div>
          <div className="w-full flex justify-center h-[300px]  mt-3">
            <Line
              options={schemeCapitalOptions}
              data={schemeCapitalData}
              className="w-full h-full"
            />
          </div>
        </Card>
      </div>
    </>
  );
}
